/**
 * Lane D3 — the board screens' desktop-only derivations
 * (INVENTORY-DESKTOP §3.4 / §3.5 / §3.6 / §5.5).
 *
 * `selectors.ts` and `selectors.desktop.ts` cover everything the three board
 * screens READ. What is missing is the handful of values the desktop template
 * derives inside its own `renderVals()` and mobile never had:
 *
 *   1. the §5.5 drag-feedback strings (`capW` / `capMl` / `op` / `bord` /
 *      `trans`, the row border and `min-height`, the TO-DO rail border) — pure
 *      functions of the screen-local drag state, so they take it as arguments
 *      rather than reading a store that does not hold it (§5.6);
 *   2. the UNFILTERED target-row id arrays the `beforeId → idx` conversion has
 *      to run against — this is a correctness issue, not a convenience:
 *      `setIntAt` / `placeTierAt` splice into rows they rebuild from the FULL
 *      list (`slices/list.ts:143`, `slices/boards.ts:99`), while `interestsVM`
 *      / `gotyVM` only ever hand the screen the FILTERED rows. Feeding an index
 *      counted over a filtered row into an unfiltered splice silently reorders
 *      the wrong games as soon as a filter is on;
 *   3. the two desktop-only copy values the board screens need that
 *      `selectors.desktop.ts` does not already carry (`bigFs`, `steamTitle`).
 *
 * Everything here is pure and closure-free (PORTING-NOTES P2-01); the drag-math
 * half is unit-tested in `selectors.d3.test.ts` against the same store
 * ordering functions `dragHtml5.test.ts` uses.
 */

import { intRowIds, isCall, LVL, tierRowIds } from '../lib/logic.ts';
import type { IntRowKey } from '../lib/logic.ts';
import type { GameId, SharedTier, Tier, VoteValue } from '../lib/types.ts';
import type { QdStore } from './types.ts';

// ---------------------------------------------------------------------------
// 1. row keys
// ---------------------------------------------------------------------------

/**
 * The Interests drop-target keys (§5.4). `interestsVM` emits `rowId` as the
 * call letter for the four call rows and `'w'` for the `? NO CALL YET` row; the
 * prototype's `overTier` namespaced them `int:*` because one `state.overTier`
 * field served both boards. Our drag state is per-screen, so the bare row id is
 * enough — but the vote each row writes is NOT the row id: the `?` row is
 * `rowId === 'w'` and vote `''`, because `''` cannot be a DOM attribute value.
 */
export type IntRowId = VoteValue | 'w';

/**
 * `rowId` → the vote `setIntAt` must write. Anything that is not a call letter
 * is the wait row's empty vote, `'w'` included — the guard rather than a letter
 * comparison, so a fifth call would route without editing this (T-01).
 */
export function intRowVote(rowId: string): IntRowKey {
  return isCall(rowId) ? rowId : '';
}

// ---------------------------------------------------------------------------
// 2. unfiltered drop rows — the `beforeId → idx` domain
// ---------------------------------------------------------------------------

/**
 * The target Interests row exactly as `setIntAt` will rebuild it — the full
 * list, ordered by `intOrder`, with NO filter applied.
 *
 * `beforeIdToIdx(rowIds, dragId, beforeId)` must be handed THIS array, not
 * `interestsVM(s).intBoardRows[i].games`, because `slices/list.ts#setIntAt`
 * splices into `intRows(s.list, …)`. With no filter the two are identical;
 * with a filter on, the visible row is a subsequence of this one and an index
 * counted over it lands in the wrong place.
 */
export function intDropRowIds(s: QdStore, rowId: string): GameId[] {
  const me = s.me();
  if (!me) return [];
  return intRowIds(s.list, me.k, s.intOrder[me.k] ?? [], intRowVote(rowId));
}

/**
 * The target GOTY tier exactly as `placeTierAt` will rebuild it — every
 * potential in that tier ordered by `tierOrder`, unfiltered.
 * `gotyVM` passes only the filter-visible ids to `tierRowIds`; this does not.
 */
export function tierDropRowIds(s: QdStore, t: Tier): GameId[] {
  const me = s.me();
  if (!me) return [];
  return tierRowIds(s.potentials[me.k] ?? [], s.tiers[me.k] ?? {}, s.tierOrder[me.k] ?? [], t);
}

/**
 * `true` when the dragged id currently carries one of MY calls — the interests
 * twin of `isPlacedTier`, and the same job: the TO-DO panel only invites a drop
 * it can act on. Dragging the featured (uncalled) cover over its own panel is a
 * no-op, so the panel must not light up for it.
 */
export function isCalledInt(s: QdStore, id: GameId | 0): boolean {
  if (!id) return false;
  const me = s.me();
  if (!me) return false;
  const e = s.list.find((x) => x.id === id);
  return !!e && isCall(e.votes[me.k] ?? '');
}

/** `true` when the dragged id is currently placed in a tier (§5.5 rail border). */
export function isPlacedTier(s: QdStore, id: GameId | 0): boolean {
  if (!id) return false;
  const me = s.me();
  if (!me) return false;
  return (s.tiers[me.k] ?? {})[String(id)] !== undefined;
}

// ---------------------------------------------------------------------------
// 3. §5.5 drag feedback — pure, screen-local state in / CSS strings out
// ---------------------------------------------------------------------------

/** The capsule width at rest — 116px on desktop, 104px on mobile (§3.4). */
export const CAP_W = '116px';
/** The gap a capsule opens when it is the insertion target: 116 + 8 (§5.5). */
export const CAP_GAP_ML = '124px';
/** Applied to EVERY capsule while any drag is live — never at rest. */
export const CAP_TRANS = 'width .16s ease,margin-left .16s ease,opacity .12s ease';
/**
 * The frozen row's transition. Same 160ms slide, on `transform` instead of the
 * two layout properties — see `capsuleDragVis`'s `frozen` arm.
 */
export const CAP_TRANS_FROZEN = 'transform .16s ease,opacity .12s ease';
/** The hovered row grows to this while a drag is over it. */
export const ROW_DRAG_MIN_H = '198px';

export interface CapsuleDragVis {
  capW: string;
  capMl: string;
  op: string;
  bord: string;
  trans: string;
}

const CAP_REST: CapsuleDragVis = {
  capW: CAP_W,
  capMl: '0px',
  op: '1',
  bord: '1px solid var(--gt-border-2)',
  trans: 'none',
};

/**
 * One board capsule's drag-time appearance (§5.5, prototype 2260-2273 and the
 * byte-identical 2325-2338).
 *
 * Three states, and they are mutually exclusive in that order:
 *   dragged — collapses to zero width with `-8px` of negative margin so the
 *             row closes over it, and fades out; its border goes transparent
 *             so no 1px line survives the collapse;
 *   target  — the capsule the pointer would insert BEFORE; it slides right by
 *             a full capsule + gap to open the landing slot;
 *   idle    — resting, but still carrying the transition while a drag is live
 *             so it animates when the target moves onto or off it.
 *
 * `frozen` — THE ROW THE DRAGGED CAPSULE ITSELF LIVES IN (2026-08-28).
 *
 * That row's population does not change while the drag is open: the capsule is
 * being re-ordered inside it, not added or removed. The width/margin pair above
 * assumes those two edits cancel, and on mobile they do — one non-wrapping
 * scroller, so the 124px freed and the 124px consumed are the same line's.
 *
 * Desktop rows wrap, and across a line boundary the halves land on DIFFERENT
 * lines: one packs a capsule up, the other pushes one down, and the row re-flows
 * into a different number of lines mid-drag. An 18-game row at 9 per line went
 * 9+9 → 8+9+1, which moved the capsule under the pointer a full line away and
 * shoved the rows below it down out from under the cursor.
 *
 * So on that row the box is left exactly as it rests — no width change, no
 * margin — and the landing slot is opened with `transform` instead, which does
 * not participate in flex layout and therefore cannot re-wrap anything.
 * `dragHtml5.ts#slotShiftTransform` is the other half; this arm only has to
 * stop fighting it. The dragged capsule still fades to `0`: it is the slot in
 * flight, and holding its box is precisely what keeps the lattice still.
 */
export function capsuleDragVis(
  dragId: GameId | 0,
  id: GameId,
  overBeforeId: GameId | 0,
  frozen = false,
): CapsuleDragVis {
  if (!dragId) return CAP_REST;
  const dragged = dragId === id;
  const target = overBeforeId === id && dragId !== id;
  if (frozen) {
    return {
      capW: CAP_W,
      capMl: '0px',
      op: dragged ? '0' : '1',
      bord: dragged ? '1px solid transparent' : '1px solid var(--gt-border-2)',
      trans: CAP_TRANS_FROZEN,
    };
  }
  return {
    capW: dragged ? '0px' : CAP_W,
    capMl: dragged ? '-8px' : target ? CAP_GAP_ML : '0px',
    op: dragged ? '0' : '1',
    bord: dragged ? '1px solid transparent' : '1px solid var(--gt-border-2)',
    trans: CAP_TRANS,
  };
}

/**
 * THE ARMED BORDER MUST NOT CHANGE THE BORDER'S *WIDTH* (2026-08-28).
 *
 * Every one of the four helpers below used to answer `2px dashed <accent>`
 * while a drag was live, against a 1px resting border. A border is part of the
 * box: growing it by 1px a side takes 2px of CONTENT width away from the row on
 * the very frame the drag starts.
 *
 * The desktop rows wrap. A line holding exactly as many capsules as fit has no
 * 2px to give, so the last capsule on it wrapped down to the next line the
 * instant `dragstart` fired — and when that capsule was the one being dragged,
 * it moved out from under the pointer and Chrome CANCELLED the drag. Measured
 * on the reporter's board: `dragstart` fired, then `dragend` 11 ms later with
 * ZERO drag/dragover events and the ghost already built. It never picked up.
 *
 * That also explains the exact shape of the report. Only the LAST capsule on a
 * FULL line is undraggable; the boundary moves when the window is resized,
 * because the width is what decides how full a line is; and the same 2px is
 * charged to every row at once, so a full line in a lower tier fails
 * identically. The TO-DO panels are in it too — they are flex siblings of the
 * rows column, so widening THEIR border narrows the rows.
 *
 * So the armed state keeps the resting border's WIDTH and says what it has to
 * say through colour and style, which cost no layout. The extra weight the
 * design asks for is drawn by `dragRing` — an `outline`, which is painted
 * outside the box model and therefore cannot reflow anything.
 */
function borderArmed(resting: string, colour: string = 'var(--acc)'): string {
  const width = resting.split(' ')[0] ?? '1px';
  return width + ' dashed ' + colour;
}

/**
 * The armed row's accent ring. Spread BESIDE `border`, never instead of it:
 * `outline` does not participate in layout, and the negative offset pulls it
 * inside the border box so it reads as the design's heavier dashed frame.
 */
export const DRAG_RING = { outline: '2px dashed var(--acc)', outlineOffset: '-3px' } as const;
export const DRAG_RING_2 = { outline: '2px dashed var(--acc2)', outlineOffset: '-3px' } as const;
export const NO_RING = { outline: 'none', outlineOffset: '0' } as const;

/** `DRAG_RING` while armed, `NO_RING` otherwise — the two board row kinds. */
export function dragRing(on: boolean): typeof DRAG_RING | typeof NO_RING {
  return on ? DRAG_RING : NO_RING;
}

/** The TO-DO panels' twin, in the second accent. */
export function dragRing2(on: boolean): typeof DRAG_RING_2 | typeof NO_RING {
  return on ? DRAG_RING_2 : NO_RING;
}

/**
 * The Interests row border. `resting` is `interestsVM`'s own `bord` — on
 * desktop the mobile drag state it keys off is always null, so it is exactly
 * the prototype's resting value and is not re-derived here. While a drag is
 * live every row goes accent-dashed. The C row is no exception — a drop target
 * invites the same way wherever it lands you.
 *
 * The `'w'` arm this used to carry (`--gt-text-5` for the `?` row) went with
 * the row itself on 2026-08-24; uncalled games live in the TO-DO panel now, and
 * its border is `intTodoBord` below.
 */
export function intRowBord(resting: string, dragOn: boolean): string {
  return dragOn ? borderArmed(resting) : resting;
}

/** The GOTY tier row border — all four rows go accent-dashed during a drag. */
export function tierRowBord(dragOn: boolean): string {
  const resting = '1px solid var(--gt-border)';
  return dragOn ? borderArmed(resting) : resting;
}

/** The capsule field's `min-height` — only the row under the pointer grows. */
export function rowMinH(dragOn: boolean, overRow: string, rowKey: string): string {
  return dragOn && overRow === rowKey ? ROW_DRAG_MIN_H : '0px';
}

/**
 * The TO-DO rail border (prototype 2522). It only invites a drop when the
 * dragged game is actually placed — dragging an unplaced game (the featured
 * cover) over its own rail is a no-op, so the rail must not pretend otherwise.
 */
export function todoRailBord(dragOn: boolean, draggedIsPlaced: boolean): string {
  const resting = '1px dashed var(--gt-border-3)';
  return dragOn && draggedIsPlaced ? borderArmed(resting, 'var(--acc2)') : resting;
}

/**
 * The Interests TO-DO panel border — `todoRailBord`'s twin, same rule with the
 * interests predicate: it arms only while a CALLED game is in flight, because
 * only a called game has a call for the panel to clear.
 */
export function intTodoBord(dragOn: boolean, draggedIsCalled: boolean): string {
  const resting = '1px dashed var(--gt-border-3)';
  return dragOn && draggedIsCalled ? borderArmed(resting, 'var(--acc2)') : resting;
}

// ---------------------------------------------------------------------------
// 4. desktop-only copy / metrics the board screens need
// ---------------------------------------------------------------------------

/**
 * The Tonight rail glyph size (prototype 2200-2205). Mobile's `sharedRowMeta`
 * emits `fs` at 14px on every row; the desktop rail is 104px wide and takes the
 * bigger type. `tonightVM` carries the mobile value as `mFs`.
 *
 * All four are the same size now — the binary board's `L` and `X` rows took
 * 13/15px because they were labelled with a word and a glyph rather than a
 * letter. Kept as a record rather than a constant so a row that ever wants its
 * own size has somewhere to say so, and so a new `SharedTier` fails to compile.
 */
export const SHARED_ROW_BIG_FS: Readonly<Record<Exclude<SharedTier, ''>, string>> = {
  S: '22px',
  A: '22px',
  B: '22px',
  C: '22px',
};

/**
 * `SharedRow.key` is typed `SharedTier`, which includes the `''` "not on the
 * board" tier that `SHARED_ROW_KEYS` never emits — this is the total lookup.
 */
export function sharedRowBigFs(t: SharedTier): string {
  return t === '' ? SHARED_ROW_BIG_FS.S : SHARED_ROW_BIG_FS[t];
}

/**
 * The GOTY auto-fill pill's `data-tip` (prototype 2525). Desktop spells out
 * where the setting lives — `gotyVM.steamSub` is the mobile string and is NOT
 * rendered on desktop, where the same copy is a tooltip instead of a sub-line.
 * Authored UPPERCASE per D0-04.
 */
export function gotySteamTitle(canSteam: boolean, checking = false): string {
  // The library check (3070) explains itself on the phone through `steamSub`.
  // Desktop has no sub-line, so the same sentence is the pill's tooltip for as
  // long as the label reads CHECKING YOUR STEAM LIBRARY.
  if (checking) {
    return 'STEAM MAKES US ASK GAME BY GAME — ABOUT A MINUTE. YOU CAN LEAVE; WE WILL FINISH WITHOUT YOU.';
  }
  return canSteam
    ? 'PULLS YOUR 2026 PLAYTIME FROM YOUR LINKED STEAM ACCOUNT'
    : 'LINK STEAM + MAKE IT PUBLIC (FRIENDS PAGE) TO AUTO-FILL';
}

/** The TO-DO rail's native `title` (§8, desktop-only). */
export const GOTY_TODO_RAIL_TITLE = 'DROP A PLACED GAME HERE TO SEND IT BACK TO YOUR TO-DO';

/** The `SKIP FOR NOW ↻` tooltip (§8, desktop-only). */
export const GOTY_SKIP_TIP = 'NO PLACEMENT — CYCLES TO THE BACK OF YOUR TO-DO';

/**
 * The Interests list call-button tooltips (§3.5, desktop-only) — one per call,
 * read off `LVL` so the tooltip and the letter it explains cannot disagree.
 *
 * The binary pair said `NOT INTERESTED — CANCELS A ▲`, which was true of a
 * board where `eff = inN - outN`. Nothing cancels anything now: a `c` weighs 0
 * and drags the average down, so the tooltip is the label and no more.
 *
 * `interestsVM`'s `calls[].tip` carries the same string per button, which is
 * what upstream's markup binds; this record is for a screen that needs the copy
 * without a row in hand.
 */
export const INT_VOTE_TIPS: Readonly<Record<VoteValue, string>> = {
  s: LVL.s.t,
  a: LVL.a.t,
  b: LVL.b.t,
  c: LVL.c.t,
};
